import { useEffect, useRef, useState } from "react";
import { Camera, Loader2, CircleAlert, ImageOff, CircleCheck } from "lucide-react";
import { api, errMessage } from "@/lib/api";

const TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
const MAX = 10 * 1024 * 1024;

export const AuthImage = ({ fileId, alt, className, testId }) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url = null;
    let cancelled = false;
    setFailed(false);
    if (!fileId) return undefined;
    api.get(`/files/${fileId}`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileId]);
  if (failed) return <div data-testid={testId} className={`flex items-center justify-center gap-2 bg-muted text-xs text-muted-foreground ${className}`}><ImageOff className="h-4 w-4" /> Photo indisponible</div>;
  if (!src) return <div data-testid={testId} className={`flex items-center justify-center bg-muted ${className}`}><Loader2 className="h-4 w-4 animate-spin" /></div>;
  return <img data-testid={testId} src={src} alt={alt} className={`object-cover ${className}`} />;
};

export const PhotoInput = ({ linkedType, linkedId, value, onChange, label, testId, error, disabled }) => {
  const ref = useRef(null);
  const [state, setState] = useState({ status: "idle", msg: "" });
  const [preview, setPreview] = useState(null);

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!TYPES.includes(file.type)) return setState({ status: "error", msg: "Format non accepté (JPEG, PNG, WEBP ou HEIC)." });
    if (file.size > MAX) return setState({ status: "error", msg: "Photo trop volumineuse (10 Mo maximum)." });
    setPreview(URL.createObjectURL(file));
    setState({ status: "uploading", msg: "" });
    const form = new FormData();
    form.append("file", file);
    form.append("linked_type", linkedType);
    form.append("linked_id", linkedId);
    try {
      const { data } = await api.post("/files", form);
      setState({ status: data.pending ? "queued" : "done", msg: "" });
      onChange(data.id);
    } catch (err) {
      setState({ status: "error", msg: errMessage(err) });
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <div className={`h-20 w-20 shrink-0 overflow-hidden rounded-lg border ${error ? "border-red-500" : "border-border"}`}>
          {preview ? <img src={preview} alt="" className="h-full w-full object-cover" />
            : value ? <AuthImage fileId={value} alt={label} className="h-full w-full" />
            : <div className="flex h-full w-full items-center justify-center bg-muted"><Camera className="h-6 w-6 text-muted-foreground" /></div>}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{label}</p>
          <button
            type="button"
            data-testid={testId}
            disabled={disabled || state.status === "uploading"}
            onClick={() => ref.current?.click()}
            className="mt-1.5 inline-flex min-h-[44px] items-center gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-3 text-sm font-semibold text-amber-200 transition-colors hover:bg-amber-500/20 disabled:opacity-50"
          >
            {state.status === "uploading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            {state.status === "uploading" ? "Envoi en cours…" : value ? "Remplacer la photo" : "Prendre / choisir une photo"}
          </button>
          {state.status === "done" && <p data-testid={`${testId}-done`} className="mt-1 flex items-center gap-1 text-xs text-emerald-300"><CircleCheck className="h-3.5 w-3.5" /> Photo envoyée</p>}
          {state.status === "queued" && <p data-testid={`${testId}-queued`} className="mt-1 flex items-center gap-1 text-xs text-amber-300"><CircleCheck className="h-3.5 w-3.5" /> Photo enregistrée sur l'appareil (envoi en attente)</p>}
          {state.status === "error" && <p data-testid={`${testId}-error`} role="alert" className="mt-1 flex items-center gap-1 text-xs text-red-300"><CircleAlert className="h-3.5 w-3.5" /> {state.msg}</p>}
        </div>
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testId}-file`} />
    </div>
  );
};
